import React from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, useTheme, Avatar } from '@mui/material';
import { format } from 'date-fns';
import { Bot, User } from 'lucide-react';
import { ChatMessage } from '../../types/chat';
import { useChatHistory } from '../../hooks/chat/useChatHistory';

interface ChatHistoryProps {
  connectionId: string;
}

export const Message = ({ message }: { message: ChatMessage }) => {
  const theme = useTheme();
  const isAI = message.type === 'ai';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isAI ? 'row' : 'row-reverse',
      }}
    >
      <Avatar
        sx={{
          bgcolor: isAI ? theme.palette.primary.main : theme.palette.secondary.main,
          width: 32,
          height: 32,
        }}
      >
        {isAI ? <Bot size={16} /> : <User size={16} />}
      </Avatar>
      <Box
        sx={{
          maxWidth: '80%',
          backgroundColor: isAI
            ? `${theme.palette.primary.main}15`
            : `${theme.palette.secondary.main}15`,
          borderRadius: 2,
          p: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 12,
            [isAI ? 'left' : 'right']: -6,
            width: 0,
            height: 0,
            borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent',
            [isAI ? 'borderRight' : 'borderLeft']: `6px solid ${
              isAI ? `${theme.palette.primary.main}15` : `${theme.palette.secondary.main}15`
            }`,
          },
        }}
      >
        <Typography variant="body2">{message.text}</Typography>
      </Box>
    </Box>
  );
};

const ChatHistory: React.FC<ChatHistoryProps> = ({ connectionId }) => {
  const { chatHistory, isLoading, error } = useChatHistory(connectionId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (chatHistory.length === 0) {
    return <Alert severity="info">No chat history available for this booking.</Alert>;
  }

  return (
    <Box>
      {chatHistory.map(chat => (
        <Paper key={chat.uuid} elevation={0} sx={{ p: 3, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Conversation started at {format(new Date(chat.updatedAt), 'MMM d, yyyy h:mm a')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            {chat.chatLogs.map((log, index) => (
              <Message key={index} message={log} />
            ))}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default ChatHistory;
